import { Fragment, useEffect, useMemo, useState } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-toastify';

import { getAllBusinesses, postBusinessCreditChange } from '../api/services/Business.js';
import Loader from '../components/Loader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import useAxiosPrivate from '../hooks/useAxiosPrivate';

const Businesses = () => {
  const axiosPrivate = useAxiosPrivate();
  const [businesses, setBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [creditsInput, setCreditsInput] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const businessesUrl = getAllBusinesses();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axiosPrivate.get(businessesUrl);
        setBusinesses(response?.data ?? []);
      } catch (err) {
        console.error(err);
        setError('Failed to load businesses.');
        toast.error('Failed to load businesses.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [axiosPrivate, businessesUrl]);

  const filteredBusinesses = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return businesses;

    return businesses.filter((b) => {
      const nameMatch = (b.name || '').toLowerCase().includes(q);
      const users = b.users || b.Users || [];
      const emailMatch = users.some((u) => (u.email || '').toLowerCase().includes(q));
      return nameMatch || emailMatch;
    });
  }, [businesses, searchQuery]);

  const handleOpenAdjust = (business) => {
    setSelectedBusiness(business);
    setCreditsInput('');
    setCommentInput('');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setSelectedBusiness(null);
  };

  const handleSubmitCreditChange = async () => {
    if (!selectedBusiness) return;

    const credits = Number(creditsInput);
    if (!creditsInput || Number.isNaN(credits) || credits === 0) {
      toast.error('Enter a non-zero number (positive to add, negative to deduct).');
      return;
    }

    const comment = commentInput.trim();
    if (!comment) {
      toast.error('Comment is required.');
      return;
    }

    setSubmitting(true);
    try {
      await axiosPrivate.post(postBusinessCreditChange(selectedBusiness.id), { credits, comment });
      toast.success('Credits updated.');
      const response = await axiosPrivate.get(businessesUrl);
      setBusinesses(response?.data ?? []);
      handleCloseModal();
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.message || 'Could not update credits.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-blue">Businesses</h1>
          <p className="text-sm text-slate-600">Search businesses and apply manual credit changes</p>
        </div>

        {error && !loading ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="max-w-md space-y-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="business-search">
            Search
          </label>
          <Input
            id="business-search"
            type="search"
            placeholder="Business name or user email…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search businesses by name or email"
            autoComplete="off"
            disabled={loading}
          />
        </div>

        <div className="relative mt-2 max-h-[800px] overflow-x-auto rounded-3xl border border-gray-200">
          {loading ? (
            <div className="flex w-full items-center justify-center py-20">
              <Loader />
            </div>
          ) : error && businesses.length === 0 ? (
            <div className="flex w-full items-center justify-center py-12">
              <p className="text-gray-500">Could not load businesses.</p>
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="p-3 font-semibold">Name</th>
                  <th className="p-3 font-semibold">Emails</th>
                  <th className="p-3 font-semibold">Credits</th>
                  <th className="p-3 font-semibold">Currency</th>
                  {/* <th className="p-3 font-semibold">Location</th> */}
                  <th className="p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBusinesses.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="h-24 px-3 text-left text-slate-500">
                      {searchQuery.trim() ? 'No businesses match your search.' : 'No businesses found.'}
                    </td>
                  </tr>
                ) : (
                  filteredBusinesses.map((b) => {
                    const users = b.users || b.Users || [];
                    const emails = users.map((u) => u.email).filter(Boolean);
                    const emailDisplay = emails.length ? emails.join(', ') : '—';

                    return (
                      <tr key={b.id} className="border-t border-slate-100">
                        <td className="p-3 font-medium">{b.name}</td>
                        <td className="max-w-xs truncate p-3 text-slate-600" title={emailDisplay}>
                          {emailDisplay}
                        </td>
                        <td className="p-3">{b.credits}</td>
                        <td className="p-3">{b.currency}</td>
                        {/* <td className="p-3 text-slate-600">
                          {b.address}, {b.postCode}
                          {b.country ? ` · ${b.country.countryName}` : ''}
                        </td> */}
                        <td className="p-3">
                          <Button
                            type="button"
                            variant="outline"
                            className="shrink-0 whitespace-nowrap border-blue text-blue"
                            onClick={() => handleOpenAdjust(b)}>
                            Adjust credits
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Transition appear show={modalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={handleCloseModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="overflow-y-auto fixed inset-0">
            <div className="flex justify-center items-center p-4 min-h-full">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="p-6 space-y-4 w-full max-w-md text-left bg-white rounded-xl shadow-xl">
                  <Dialog.Title className="text-lg font-bold text-blue">Adjust credits</Dialog.Title>
                  {selectedBusiness ? (
                    <p className="text-sm text-slate-600">
                      {selectedBusiness.name} — current balance: <span className="font-semibold">{selectedBusiness.credits}</span> {selectedBusiness.currency}
                    </p>
                  ) : null}

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="credits-change">
                      Credits change
                    </label>
                    <Input
                      id="credits-change"
                      type="number"
                      inputMode="decimal"
                      placeholder="e.g. 100 or -50"
                      value={creditsInput}
                      onChange={(e) => setCreditsInput(e.target.value)}
                      disabled={submitting}
                    />
                    <p className="text-xs text-slate-500">Positive adds credits, negative deducts.</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="credit-comment">
                      Comment
                    </label>
                    <textarea
                      id="credit-comment"
                      className="w-full min-h-[96px] rounded-md border border-slate-200 px-3 py-2 text-sm"
                      placeholder="Reason for this change"
                      value={commentInput}
                      onChange={(e) => setCommentInput(e.target.value)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="flex gap-2 justify-end pt-2">
                    <Button type="button" variant="ghost" onClick={handleCloseModal} disabled={submitting}>
                      Cancel
                    </Button>
                    <Button type="button" className="text-white bg-blue" onClick={handleSubmitCreditChange} disabled={submitting}>
                      {submitting ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Businesses;
